/**
 * pages/NarsaFines.jsx - NARSA Fines Management (V2)
 *
 * NARSA traffic fine tracking, 30-day declaration compliance,
 * Robot 6 auto-matching, and dénonciation generation.
 * Uses ui.jsx components + CSS variables exclusively.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { narsa as narsaApi, fleet as fleetApi } from '../services/api';
import { formatDate as formatDateLocale, formatNumber } from '../utils/formatters';
import {
  PageHeader, Badge, Card, EmptyState, SkeletonTable,
  StatCard, GlassModal,
} from '../components/ui';

// ── Status config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  detected:  { labelKey: 'narsa.detected',       variant: 'warning' },
  pending:   { labelKey: 'narsa.pending',         variant: 'warning' },
  notified:  { labelKey: 'narsa.statusNotified',  variant: 'warning' },
  matched:   { labelKey: 'narsa.statusMatched',   variant: 'info' },
  declared:  { labelKey: 'narsa.statusDeclared',  variant: 'success' },
  paid:      { labelKey: 'narsa.paid',            variant: 'neutral' },
  contested: { labelKey: 'narsa.contested',       variant: 'danger' },
};

// ── Filter pills ─────────────────────────────────────────────────────
const FILTERS = [
  { key: 'all',       labelKey: 'common.all' },
  { key: 'detected',  labelKey: 'narsa.detectedPlural' },
  { key: 'matched',   labelKey: 'narsa.assignedPlural' },
  { key: 'declared',  labelKey: 'narsa.declared' },
  { key: 'paid',      labelKey: 'narsa.paidPlural' },
  { key: 'contested', labelKey: 'narsa.contestedPlural' },
];

const INFRACTION_LABEL_KEYS = {
  speed: 'narsa.speed', speeding: 'narsa.speed',
  red_light: 'narsa.redLight', parking: 'narsa.parking',
};

// ── Helpers ──────────────────────────────────────────────────────────

function maskPhone(phone) {
  if (!phone || phone.length < 4) return '-';
  return '•••• ' + phone.slice(-4);
}

// ── Urgency indicator ────────────────────────────────────────────────
function UrgencyDot({ daysRemaining }) {
  const { t } = useTranslation();
  if (daysRemaining == null) return null;
  const d = Number(daysRemaining);
  const abbr = t('narsa.daysAbbr');
  let color = 'var(--green)';
  let label = `${d}${abbr}`;
  if (d <= 0) { color = 'var(--red)'; label = t('narsa.overdue'); }
  else if (d <= 7) { color = 'var(--red)'; label = `${d}${abbr}`; }
  else if (d <= 15) { color = 'var(--orange)'; }

  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: d <= 7 ? 'var(--status-alert-bg)' : d <= 15 ? 'var(--status-maintenance-bg)' : 'var(--status-available-bg)',
      color, fontSize: 'var(--text-label)', fontWeight: 700, fontFamily: 'var(--font-mono)',
    }}>{label}</span>
  );
}

// ── Filter pill ──────────────────────────────────────────────────────
function FilterPill({ label, count, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', fontSize: 'var(--text-caption)', fontWeight: active ? 600 : 400,
      background: active ? 'var(--gold-dim)' : 'transparent',
      color: active ? 'var(--gold)' : 'var(--text-secondary)',
      border: `1px solid ${active ? 'var(--border-gold)' : 'var(--border)'}`,
      borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit',
      display: 'flex', alignItems: 'center', gap: 6,
      transition: 'all var(--duration-fast) var(--ease-smooth)', whiteSpace: 'nowrap',
    }}>
      {label}
      {count !== undefined && (
        <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-mono)', color: active ? 'var(--gold-light)' : 'var(--text-muted)' }}>{count}</span>
      )}
    </button>
  );
}

// ── Add Fine Modal ───────────────────────────────────────────────────
function AddFineModal({ open, vehicles, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    vehicle_id: '', fine_reference: '', infraction_type: 'speed',
    infraction_date: new Date().toISOString().slice(0, 10),
    fine_amount_mad: '', location: '', speed_detected: '', speed_limit: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setError(null);
    if (!form.vehicle_id || !form.fine_reference || !form.fine_amount_mad) { setError(t('narsa.requiredFields')); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        fine_amount_mad: parseFloat(form.fine_amount_mad),
        speed_detected: form.speed_detected ? parseInt(form.speed_detected) : null,
        speed_limit: form.speed_limit ? parseInt(form.speed_limit) : null,
      });
      onClose();
    } catch (e) { setError(e.message || t('narsa.registerError')); }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px', background: 'var(--elevated)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text)', fontSize: 'var(--text-body)', fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = {
    fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block',
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('narsa.registerFineTitle')} subtitle={t('narsa.registerFineDesc')} width={520}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {error && (
          <div style={{ padding: 'var(--space-md)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-body)' }}>{error}</div>
        )}
        <div>
          <label style={labelStyle}>{t('narsa.vehicle')}</label>
          <select value={form.vehicle_id} onChange={e => set('vehicle_id', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">{t('narsa.selectVehicle')}</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate_number} - {v.make} {v.model}</option>)}
          </select>
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('narsa.reference')}</label>
            <input value={form.fine_reference} onChange={e => set('fine_reference', e.target.value)} placeholder={t('narsa.reference')} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('narsa.infractionType')}</label>
            <select value={form.infraction_type} onChange={e => set('infraction_type', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="speed">{t('narsa.speed')}</option>
              <option value="red_light">{t('narsa.redLight')}</option>
              <option value="parking">{t('narsa.parking')}</option>
              <option value="other">{t('narsa.other')}</option>
            </select>
          </div>
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('narsa.infractionDate')}</label>
            <input type="date" value={form.infraction_date} onChange={e => set('infraction_date', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('narsa.amount')} (MAD)</label>
            <input type="number" value={form.fine_amount_mad} onChange={e => set('fine_amount_mad', e.target.value)} placeholder="700" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t('narsa.location')}</label>
          <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="Ex: Autoroute Oujda-Nador km 42" style={inputStyle} />
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('narsa.speedDetected')}</label>
            <input type="number" value={form.speed_detected} onChange={e => set('speed_detected', e.target.value)} placeholder="km/h" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div>
            <label style={labelStyle}>{t('narsa.speedLimit')}</label>
            <input type="number" value={form.speed_limit} onChange={e => set('speed_limit', e.target.value)} placeholder="km/h" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
      </div>
      <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ padding: '10px 20px', height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.cancel')}</button>
        <button onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 24px', height: 44, background: submitting ? 'var(--text-muted)' : 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
          {submitting ? t('narsa.registering') : t('narsa.registerFine')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── Declare Modal ────────────────────────────────────────────────────
function DeclareModal({ open, fine, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [declareError, setDeclareError] = useState(null);

  const handleConfirm = async () => {
    setConfirming(true);
    setDeclareError(null);
    try { await onConfirm(fine.id); onClose(); } catch (e) { setDeclareError(e.message || t('common.error')); }
    setConfirming(false);
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('narsa.denunciation')} subtitle={t('narsa.denunciationDesc')} width={440}>
      <div style={{ padding: 'var(--space-xl)' }}>
        <div style={{ background: 'var(--status-maintenance-bg)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-lg)', marginBottom: 'var(--space-lg)', fontSize: 'var(--text-body)', color: 'var(--text)', lineHeight: 1.6 }}>
          {fine && <>
            <div><strong>{t('narsa.reference')} :</strong> {fine.fine_reference}</div>
            <div><strong>{t('narsa.vehicle')} :</strong> {fine.plate_number}</div>
            {fine.renter_name && <div><strong>{t('narsa.driver')} :</strong> {fine.renter_name}</div>}
            <div><strong>{t('narsa.amount')} :</strong> <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)' }}>{formatNumber(fine.fine_amount_mad)} MAD</span></div>
            {fine.days_remaining != null && <div style={{ marginTop: 'var(--space-sm)' }}><strong>{t('narsa.deadline')} :</strong> <UrgencyDot daysRemaining={fine.days_remaining} /></div>}
          </>}
        </div>
        <p style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 0 }}>
          {t('narsa.denunciationAction')}
        </p>
      </div>
      {declareError && (
        <div style={{ margin: '0 var(--space-xl) var(--space-sm)', padding: '10px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: '#C53030', lineHeight: 1.4 }}>
          {declareError}
        </div>
      )}
      <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.cancel')}</button>
        <button onClick={handleConfirm} disabled={confirming} style={{ flex: 1, height: 44, background: confirming ? 'var(--text-muted)' : 'var(--orange)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: confirming ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
          {confirming ? t('narsa.declaring') : t('narsa.confirmDenunciation')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── Declaration Packet Modal ────────────────────────────────────────
function DeclarationPacketModal({ open, fine, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [packet, setPacket] = useState(null);
  const [packetError, setPacketError] = useState(null);
  const [manualICE, setManualICE] = useState('');
  const [manualLicense, setManualLicense] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && fine) {
      setLoading(true);
      setPacket(null);
      setPacketError(null);
      setCopied(false);
      setManualICE('');
      setManualLicense('');
      narsaApi.declarationPacket(fine.id)
        .then(data => { setPacket(data); setLoading(false); })
        .catch(e => { setPacketError(e.message || t('common.error')); setLoading(false); });
    }
  }, [open, fine, t]);

  const handleCopy = () => {
    if (!packet) return;
    let text = packet.formatted_text;
    if (manualICE) text = text.replace('_____________________ (manual entry)', manualICE);
    if (manualLicense) text = text.replace('_____________________ (manual entry)', manualLicense);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('narsa.declarationPacket')} subtitle={t('narsa.declarationPacketSubtitle')} width={720}>
      {loading && (
        <div style={{ padding: 'var(--space-xl)', textAlign: 'center', color: 'var(--text-muted)' }}>{t('narsa.declarationLoading')}</div>
      )}
      {!loading && packetError && (
        <div style={{ padding: 'var(--space-xl)' }}>
          <div style={{ padding: '10px 14px', marginBottom: 'var(--space-lg)', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: '#C53030', lineHeight: 1.4 }}>
            {packetError}
          </div>
          <button onClick={onClose} style={{ width: '100%', padding: 12, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.close')}</button>
        </div>
      )}
      {!loading && packet && (
        <>
          <div style={{ padding: 'var(--space-xl)', maxHeight: '60vh', overflowY: 'auto' }}>
            {/* Manual fields */}
            <div style={{ background: 'var(--status-maintenance-bg)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
              <div style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--gold)', marginBottom: 'var(--space-sm)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('narsa.manualFields')}
              </div>
              <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{t('narsa.iceNumber')}</label>
                  <input value={manualICE} onChange={e => setManualICE(e.target.value)} placeholder={t('narsa.iceNumberPlaceholder')} style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontFamily: 'var(--font-mono)', background: 'var(--card)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{t('narsa.licenseNumber')}</label>
                  <input value={manualLicense} onChange={e => setManualLicense(e.target.value)} placeholder={t('narsa.licenseNumberPlaceholder')} style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontFamily: 'var(--font-mono)', background: 'var(--card)' }} />
                </div>
              </div>
            </div>

            {/* Formatted packet preview */}
            <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-lg)' }}>
              <div style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 'var(--space-sm)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('narsa.declarationFilled')}
              </div>
              <pre style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text)', fontFamily: 'var(--font-mono)', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {packet.formatted_text}
              </pre>
            </div>
          </div>

          {/* Actions */}
          <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', gap: 'var(--space-md)' }}>
            <button onClick={onClose} style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.close')}</button>
            <button onClick={handleCopy} style={{ flex: 1, height: 44, background: copied ? 'var(--green)' : 'var(--status-rented)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {copied ? t('narsa.copied') : t('narsa.copyPacket')}
            </button>
          </div>
        </>
      )}
    </GlassModal>
  );
}

// ── Zero-value stats fallback (API down = show nothing, not fake data) ──
const EMPTY_STATS = { total_fines: 0, open_count: 0, total_exposure_mad: 0, declared_count: 0, urgent_count: 0, overdue_count: 0 };

// ── Main Component ───────────────────────────────────────────────────
export default function NarsaFines() {
  const { t } = useTranslation();
  const [fines, setFines] = useState([]);
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [declareFine, setDeclareFine] = useState(null);
  const [packetFine, setPacketFine] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const results = await Promise.allSettled([
        narsaApi.fines(),
        narsaApi.stats(),
        fleetApi.vehicles(),
      ]);

      if (ctrl.signal.aborted) return;

      const [finesRes, statsRes, vehiclesRes] = results;

      if (finesRes.status === 'fulfilled' && finesRes.value) setFines(finesRes.value.fines || []);
      if (statsRes.status === 'fulfilled' && statsRes.value) setStats(statsRes.value);
      if (vehiclesRes.status === 'fulfilled' && vehiclesRes.value) setVehicles(vehiclesRes.value.vehicles || []);

      if (finesRes.status === 'rejected') {
        setFines([]);
        setStats(null);
        setError(finesRes.reason?.message || t('system.serviceUnavailable'));
      } else {
        setError(null);
      }
    } catch (e) {
      if (!ctrl.signal.aborted) { setError(e.message); setFines([]); setStats(null); }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => { clearInterval(iv); abortRef.current?.abort(); };
  }, [fetchData]);

  const handleCreateFine = async (data) => {
    await narsaApi.createFine(data);
    fetchData();
  };

  const handleDeclare = async (fineId) => {
    await narsaApi.declareFine(fineId);
    fetchData();
  };

  const handleMatch = async (fineId) => {
    try { await narsaApi.matchFine(fineId); fetchData(); } catch (e) { setError(e.message || t('common.error')); }
  };

  // Filtering + sort by urgency (most urgent first)
  const filtered = fines
    .filter(f => {
      if (filter === 'all') return true;
      if (filter === 'detected') return ['detected', 'pending', 'notified'].includes(f.status);
      if (filter === 'matched') return f.status === 'matched';
      if (filter === 'declared') return f.status === 'declared';
      return f.status === filter;
    })
    .sort((a, b) => {
      // Active/unresolved first, sorted by days remaining ascending
      const aActive = ['detected', 'pending', 'notified', 'matched'].includes(a.status);
      const bActive = ['detected', 'pending', 'notified', 'matched'].includes(b.status);
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      const aD = a.days_remaining ?? 999;
      const bD = b.days_remaining ?? 999;
      return aD - bD;
    });

  const fs = stats || EMPTY_STATS;
  const counts = {
    all: fines.length,
    detected: fines.filter(f => ['detected', 'pending', 'notified'].includes(f.status)).length,
    matched: fines.filter(f => f.status === 'matched').length,
    declared: fines.filter(f => f.status === 'declared').length,
    paid: fines.filter(f => f.status === 'paid').length,
    contested: fines.filter(f => f.status === 'contested').length,
  };

  // Fines that need action today (≤7 days or overdue)
  const urgentFines = fines.filter(f =>
    ['detected', 'pending', 'notified', 'matched'].includes(f.status) &&
    f.days_remaining != null && f.days_remaining <= 7
  );

  const addButton = (
    <button onClick={() => setShowAddModal(true)} style={{
      padding: '10px 20px', height: 44, background: 'var(--red)', color: '#fff', border: 'none',
      borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: 'pointer',
      fontFamily: 'inherit', letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>+ {t('narsa.addFine')}</button>
  );

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      <PageHeader title={t('narsa.title')} subtitle={`${fines.length} ${t('narsa.finesRegistered')}`} action={addButton} />

      {/* Stat cards — urgent_count replaces declared (declared is now a filter) */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
        <StatCard label={t('narsa.totalFines')} value={fs.total_fines || fines.length} index={0} />
        <StatCard label={t('narsa.pending')} value={fs.open_count || 0} trendUp={false} index={1} />
        <StatCard label={t('narsa.exposure')} value={`${formatNumber(fs.total_exposure_mad || 0)} MAD`} trendUp={false} index={2} />
        <StatCard
          label={t('narsa.urgentLabel')}
          value={fs.urgent_count || urgentFines.length}
          trendUp={false}
          index={3}
        />
      </div>

      {/* Urgent deadline alert — shown when open fines are ≤7 days from deadline */}
      {urgentFines.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-md)',
          padding: 'var(--space-md) var(--space-lg)',
          background: 'var(--status-alert-bg)',
          border: '1px solid var(--red)',
          borderLeft: '4px solid var(--red)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-xl)',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'var(--red)', display: 'inline-block',
            flexShrink: 0, marginTop: 5,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', marginBottom: 4 }}>
              {urgentFines.length === 1
                ? t('narsa.urgentBannerSingular')
                : t('narsa.urgentBannerPlural', { count: urgentFines.length })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {urgentFines.map(f => f.plate_number).filter(Boolean).join(' · ')}{' '}
              — {t('narsa.urgentNote')}
            </div>
          </div>
          <button
            onClick={() => setFilter('detected')}
            style={{
              flexShrink: 0, padding: '5px 14px',
              background: 'transparent', border: '1px solid var(--red)',
              borderRadius: 'var(--radius-sm)', color: 'var(--red)',
              fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {t('common.view')}
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-xl)', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <FilterPill key={f.key} label={t(f.labelKey)} count={counts[f.key]} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 'var(--space-lg)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius)', color: 'var(--red)', fontSize: 'var(--text-body)', marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{t('common.errorPrefix')} {error}</span>
          <button onClick={() => { setError(null); setLoading(true); fetchData(); }} style={{ padding: '6px 16px', background: 'transparent', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-caption)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.retry')}</button>
        </div>
      )}

      {/* Loading */}
      {loading && <SkeletonTable rows={5} />}

      {/* Empty */}
      {!loading && fines.length === 0 && (
        <Card><EmptyState icon={null} title={t('narsa.noFines')} description={t('narsa.noFinesDesc')} /></Card>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="responsive-table" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t('narsa.date'), t('narsa.vehicle'), t('narsa.infraction'), t('narsa.amount'), t('narsa.driver'), t('narsa.deadline'), t('common.status'), t('common.actions')].map(h => (
                  <th key={h} style={{ padding: '12px 16px', fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((fine, i) => {
                const sc = STATUS_CONFIG[fine.status] || STATUS_CONFIG.pending;
                const isUnassigned = ['detected', 'pending', 'notified'].includes(fine.status);

                return (
                  <tr key={fine.id || i} style={{
                    borderLeft: isUnassigned ? '3px solid var(--orange)' : '3px solid transparent',
                    transition: 'background var(--duration-fast) var(--ease-smooth)',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td data-label={t('narsa.date')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>{formatDateLocale(fine.infraction_date)}</td>
                    <td data-label={t('narsa.vehicle')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--gold)', fontSize: 'var(--text-body)', letterSpacing: 1 }}>{fine.plate_number}</div>
                      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{fine.make} {fine.model}</div>
                    </td>
                    <td data-label={t('narsa.infraction')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--text)' }}>
                      {INFRACTION_LABEL_KEYS[fine.infraction_type] ? t(INFRACTION_LABEL_KEYS[fine.infraction_type]) : fine.infraction_type}
                      {fine.speed_detected && fine.speed_limit && (
                        <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{fine.speed_detected}/{fine.speed_limit} km/h</div>
                      )}
                    </td>
                    <td data-label={t('narsa.amount')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--gold)' }}>
                      {formatNumber(fine.fine_amount_mad)} MAD
                    </td>
                    <td data-label={t('narsa.driver')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: fine.renter_name ? 'var(--text)' : 'var(--text-muted)' }}>
                      {fine.renter_name || t('narsa.unidentified')}
                    </td>
                    <td data-label={t('narsa.deadline')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <UrgencyDot daysRemaining={fine.days_remaining} />
                    </td>
                    <td data-label={t('common.status')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <Badge label={t(sc.labelKey)} variant={sc.variant} />
                    </td>
                    <td data-label={t('common.actions')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {isUnassigned && (
                          <button onClick={() => handleMatch(fine.id)} style={{ padding: '4px 10px', background: 'var(--status-rented-bg)', color: 'var(--status-rented)', border: '1px solid rgba(91,155,213,0.3)', borderRadius: 6, fontSize: 'var(--text-label)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>{t('narsa.identify')}</button>
                        )}
                        {['detected', 'pending', 'notified', 'matched'].includes(fine.status) && (
                          <>
                            <button onClick={() => setPacketFine(fine)} style={{ padding: '4px 10px', background: 'var(--status-rented-bg)', color: 'var(--status-rented)', border: '1px solid rgba(91,155,213,0.3)', borderRadius: 6, fontSize: 'var(--text-label)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>{t('narsa.packet')}</button>
                            <button onClick={() => setDeclareFine(fine)} style={{ padding: '4px 10px', background: 'var(--status-maintenance-bg)', color: 'var(--status-maintenance)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 6, fontSize: 'var(--text-label)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>{t('narsa.declare')}</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* No results for filter */}
      {!loading && filtered.length === 0 && fines.length > 0 && (
        <Card><EmptyState icon={null} title={t('common.noResults')} description={t('narsa.noResultsDesc')} ctaLabel={t('common.reset')} onCta={() => setFilter('all')} /></Card>
      )}

      {/* Modals */}
      <AddFineModal open={showAddModal} vehicles={vehicles} onClose={() => setShowAddModal(false)} onSubmit={handleCreateFine} />
      <DeclareModal open={!!declareFine} fine={declareFine} onClose={() => setDeclareFine(null)} onConfirm={handleDeclare} />
      <DeclarationPacketModal open={!!packetFine} fine={packetFine} onClose={() => setPacketFine(null)} />
    </div>
  );
}
